import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, RotateCcw, Save, MonitorSmartphone, Globe, Image as ImageIcon, ImagePlus, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getStoredToken } from "@/lib/api";
import { loadBranding } from "@/lib/branding";
import { adminUploadBrandingVideo } from "@/lib/adminBrandingVideo";
import VideoSlotUploader from "@/components/VideoSlotUploader";

const VIDEO_SLOT_LABELS = { upload: "Upload dialog", analyze: "Analyze overlay", estimate: "Estimate overlay" };
const BRAND_IDS = ["elite", "steeltooth", "custom"];
const BRAND_BLURB = {
  elite: "The Elite Mechanical portal",
  steeltooth: "Steeltooth Contracting",
  custom: "The Jasper portal — rename it below if needed",
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Superadmin-only branding panel: three switchable brand sets (Elite /
 * Steeltooth / Custom). Flipping a brand instantly swaps the sign-in page,
 * browser title, favicons, installed-app icons, in-app logo and sign-in
 * photo — so the same app can run on another VPS as a different company.
 * All assets are served dynamically from the backend; no rebuild needed.
 */
export default function BrandingPanel() {
  const [branding, setBranding] = useState(null);
  const [appName, setAppName] = useState("");
  const [browserTitle, setBrowserTitle] = useState("");
  const [loginPage, setLoginPage] = useState("elite");
  const [customLabel, setCustomLabel] = useState("Jasper");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [heroFile, setHeroFile] = useState(null);
  const [heroPreview, setHeroPreview] = useState(null);
  const [savingText, setSavingText] = useState(false);
  const [switching, setSwitching] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [videoBusy, setVideoBusy] = useState({});
  const fileRef = useRef(null);
  const heroRef = useRef(null);

  const active = branding?.active_brand || "elite";
  const brands = branding?.brands || {};
  const activeLabel = brands[active]?.label || "Elite";
  const v = branding?.icon_version ? `&v=${branding.icon_version}` : "";
  const auth = { headers: { Authorization: `Bearer ${getStoredToken()}` } };

  const syncForm = (d) => {
    setBranding(d);
    setAppName(d.app_name || "");
    setBrowserTitle(d.browser_title || "");
    setLoginPage(d.login_page === "steeltooth" ? "steeltooth" : "elite");
    setCustomLabel(d.brands?.custom?.label || "Jasper");
  };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/branding`);
      syncForm(data);
    } catch {
      toast.error("Couldn't load branding settings");
    }
  };

  useEffect(() => { load(); }, []);

  const pickFile = (f, setter, previewSetter, ref) => {
    if (!f) return;
    if (!/^image\/(png|jpeg|webp)$/.test(f.type)) {
      toast.error("Use a PNG, JPG or WebP image");
      return;
    }
    setter(f);
    previewSetter(URL.createObjectURL(f));
  };

  const switchBrand = async (brand) => {
    if (brand === active || switching) return;
    setSwitching(brand);
    try {
      const { data } = await axios.put(`${API}/branding`, { active_brand: brand }, auth);
      syncForm(data);
      await loadBranding(true);
      toast.success(`${data.brands[brand].label} branding is now active — sign-in page, icons, name and photos flipped`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't switch brand");
    } finally {
      setSwitching("");
    }
  };

  const saveText = async () => {
    setSavingText(true);
    try {
      const body = { app_name: appName.trim(), browser_title: browserTitle.trim(), login_page: loginPage };
      if (active === "custom") body.label = customLabel.trim();
      const { data } = await axios.put(`${API}/branding`, body, auth);
      syncForm(data);
      await loadBranding(true);
      toast.success(`${activeLabel} branding saved`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save branding");
    } finally {
      setSavingText(false);
    }
  };

  const uploadIcon = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/branding/icon`, fd, auth);
      syncForm(data);
      setFile(null);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
      await loadBranding(true);
      toast.success(`${activeLabel} icons updated everywhere`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Icon upload failed");
    } finally {
      setUploading(false);
    }
  };

  const uploadHero = async () => {
    if (!heroFile) return;
    setUploadingHero(true);
    try {
      const fd = new FormData();
      fd.append("file", heroFile);
      const { data } = await axios.post(`${API}/branding/hero`, fd, auth);
      syncForm(data);
      setHeroFile(null);
      setHeroPreview(null);
      if (heroRef.current) heroRef.current.value = "";
      toast.success(`${activeLabel} sign-in photo updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploadingHero(false);
    }
  };

  const resetBrand = async () => {
    if (!window.confirm(`Reset ${activeLabel} back to its built-in defaults? (text, sign-in page, icons${loginPage === "steeltooth" ? ", photo" : ""})`)) return;
    setResetting(true);
    try {
      const { data } = await axios.post(`${API}/branding/reset`, {}, auth);
      syncForm(data);
      setFile(null);
      setPreview(null);
      setHeroFile(null);
      setHeroPreview(null);
      await loadBranding(true);
      toast.success(`${activeLabel} reset to defaults`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const uploadVideo = async (slot, f) => {
    setVideoBusy((p) => ({ ...p, [slot]: 1 }));
    try {
      const data = await adminUploadBrandingVideo(slot, f, (pct) =>
        setVideoBusy((p) => ({ ...p, [slot]: Math.max(1, pct) })),
      );
      if (data?.branding) setBranding(data.branding);
      await loadBranding(true);
      toast.success(`${VIDEO_SLOT_LABELS[slot]} video replaced`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Video upload failed");
    } finally {
      setVideoBusy((p) => { const n = { ...p }; delete n[slot]; return n; });
    }
  };

  const resetVideo = async (slot) => {
    try {
      const { data } = await axios.put(`${API}/branding`, { [`video_${slot}_url`]: "" }, auth);
      setBranding(data);
      await loadBranding(true);
      toast.success(`${VIDEO_SLOT_LABELS[slot]} video back to the default`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  const Spot = ({ title, sub, src, box }) => (
    <div className="rounded-md border border-white/15 bg-white/[0.03] p-3">
      <p className="text-xs font-semibold text-white/80">{title}</p>
      <p className="mt-0.5 text-[11px] text-white/40">{sub}</p>
      <div className="mt-2 flex h-16 items-center justify-center rounded bg-black/40">
        <img src={src} alt={title} className={`${box} object-contain`} data-testid={`branding-preview-${title.toLowerCase().replace(/\s+/g, "-")}`} />
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="branding-panel">
      {/* ---- brand set switch ---- */}
      <div>
        <p className="mb-1 text-xs font-bold uppercase tracking-wider text-white/50">Brand set</p>
        <p className="mb-3 text-[11px] leading-relaxed text-white/40">
          One switch flips the sign-in page, icons, images and text for the whole app. Set up each brand below, then flip —
          handy for running this same app on another server as a different company. Changes apply instantly, everywhere.
        </p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {BRAND_IDS.map((id) => {
            const cfg = brands[id] || {};
            const isActive = active === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => switchBrand(id)}
                disabled={!!switching}
                data-testid={`branding-brand-${id}`}
                aria-pressed={isActive}
                className={`relative flex items-center gap-2.5 rounded-md border p-3 text-left transition-colors ${
                  isActive ? "border-[#3b5bff] bg-[#1a3fd1]/15" : "border-white/15 bg-white/[0.03] hover:bg-white/[0.07]"
                }`}
              >
                <img
                  src={`${API}/branding/icon/favicon-32.png?brand=${id}${v}`}
                  alt=""
                  className="h-9 w-9 shrink-0 rounded-md border border-white/10 bg-black/30"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-white/90">{cfg.label || id}</p>
                  <p className="truncate text-[10px] text-white/40">{BRAND_BLURB[id]}</p>
                </div>
                {switching === id ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-white/60" />
                ) : isActive ? (
                  <span
                    className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-500/50 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-300"
                    data-testid={`branding-brand-active-${id}`}
                  >
                    <Check className="h-2.5 w-2.5" /> Active
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* ---- text branding for the active brand ---- */}
      <div className="rounded-md border border-white/15 bg-white/[0.03] p-4">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-[#8fa4f0]" />
          <p className="text-sm font-semibold text-white/90" data-testid="branding-active-heading">{activeLabel} — text & sign-in page</p>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {active === "custom" && (
            <div className="sm:col-span-2">
              <Label className="text-xs text-white/60">Brand name (what the "Custom" set is called)</Label>
              <Input
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                data-testid="branding-custom-label"
                maxLength={40}
                className="mt-1 border-white/15 bg-white/5 text-white"
                placeholder="e.g. Acme Excavating"
              />
            </div>
          )}
          <div>
            <Label className="text-xs text-white/60">App name (installed app label)</Label>
            <Input
              value={appName}
              onChange={(e) => setAppName(e.target.value)}
              data-testid="branding-app-name"
              maxLength={40}
              className="mt-1 border-white/15 bg-white/5 text-white"
              placeholder="Elite"
            />
          </div>
          <div>
            <Label className="text-xs text-white/60">Browser window title</Label>
            <Input
              value={browserTitle}
              onChange={(e) => setBrowserTitle(e.target.value)}
              data-testid="branding-browser-title"
              maxLength={60}
              className="mt-1 border-white/15 bg-white/5 text-white"
              placeholder="Elite"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs text-white/60">Sign-in page for this brand</Label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {[
                ["elite", "Elite portal", "Dark portal sign-in"],
                ["steeltooth", "Steeltooth portal", "Full-photo sign-in with logo card"],
              ].map(([id, title, sub]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setLoginPage(id)}
                  data-testid={`branding-login-${id}`}
                  aria-pressed={loginPage === id}
                  className={`rounded-md border p-2.5 text-left transition-colors ${
                    loginPage === id ? "border-[#3b5bff] bg-[#1a3fd1]/15" : "border-white/15 bg-white/[0.03] hover:bg-white/[0.07]"
                  }`}
                >
                  <p className="text-xs font-bold text-white/90">{title}</p>
                  <p className="mt-0.5 text-[10px] text-white/40">{sub}</p>
                </button>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-white/40">Which sign-in screen everyone sees while this brand is active. Applies on next page load.</p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            data-testid="branding-save-text"
            disabled={savingText}
            onClick={saveText}
            className="bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85"
          >
            <Save className="mr-1.5 h-4 w-4" /> {savingText ? "Saving…" : `Save ${activeLabel} branding`}
          </Button>
          <Button
            type="button"
            variant="outline"
            data-testid="branding-reset"
            disabled={resetting}
            onClick={resetBrand}
            className="border-red-500/40 text-red-400 hover:bg-red-500/10"
          >
            <RotateCcw className="mr-1.5 h-4 w-4" /> {resetting ? "Resetting…" : `Reset ${activeLabel} to defaults`}
          </Button>
        </div>
      </div>

      {/* ---- logo & icons for the active brand ---- */}
      <div className="rounded-md border border-white/15 bg-white/[0.03] p-4">
        <div className="flex items-start gap-3">
          <ImageIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#8fa4f0]" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white/90">{activeLabel} logo & icons</p>
            <p className="mt-1 text-xs leading-relaxed text-white/50">
              One upload updates every spot for this brand. Required: <span className="font-semibold text-white/70">square PNG</span> (JPG/WebP accepted,
              transparent background recommended), <span className="font-semibold text-white/70">at least 512 × 512 px</span>, under 5 MB.
              Applies instantly — devices that already installed the app keep the old icon until it's re-added.
            </p>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Spot title="Browser tab" sub="favicon 16/32 px" src={`${API}/branding/icon/favicon-32.png?brand=${active}${v}`} box="h-8 w-8" />
              <Spot title="Installed app icon" sub="phones & computers · 192/512 px + Apple touch icon" src={`${API}/branding/icon/icon-192.png?brand=${active}${v}`} box="h-12 w-12" />
              <Spot title="Top bar & login logo" sub="dashboard, login, notifications, broadcasts" src={`${API}/branding/brand-logo?brand=${active}${v}`} box="h-10 w-auto max-w-[140px]" />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                data-testid="branding-icon-input"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0], setFile, setPreview, fileRef)}
              />
              <Button
                type="button"
                variant="outline"
                data-testid="branding-icon-choose"
                onClick={() => fileRef.current?.click()}
                className="border-white/15 bg-white/5 text-white hover:bg-white/10"
              >
                <Upload className="mr-1.5 h-4 w-4" /> Choose image
              </Button>
              {preview && (
                <>
                  <img src={preview} alt="New logo preview" className="h-12 w-12 rounded border border-white/15 object-contain" data-testid="branding-icon-preview" />
                  <Button
                    type="button"
                    data-testid="branding-icon-upload"
                    disabled={uploading}
                    onClick={uploadIcon}
                    className="bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85"
                  >
                    {uploading ? "Uploading…" : `Upload & apply to ${activeLabel}`}
                  </Button>
                </>
              )}
            </div>
            {branding?.custom_icon && (
              <p className="mt-2 text-[11px] text-emerald-400" data-testid="branding-custom-flag">A custom icon is currently active for {activeLabel}.</p>
            )}
          </div>
        </div>
      </div>

      {/* ---- sign-in photo (blueprint page only) ---- */}
      {loginPage === "steeltooth" && (
        <div className="rounded-md border border-white/15 bg-white/[0.03] p-4" data-testid="branding-hero-section">
          <div className="flex items-start gap-3">
            <ImagePlus className="mt-0.5 h-5 w-5 shrink-0 text-[#8fa4f0]" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white/90">{activeLabel} sign-in photo</p>
              <p className="mt-1 text-xs leading-relaxed text-white/50">
                The full-screen photo behind the blueprint sign-in page. JPG/PNG/WebP, under 10 MB — landscape jobsite shots work best.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <img
                  src={`${API}/branding/hero?brand=${active}&v=${branding?.updated_at || ""}`}
                  alt="Current sign-in"
                  className="h-20 w-32 rounded-md border border-white/15 object-cover bg-black/40"
                  data-testid="branding-hero-preview"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  onLoad={(e) => { e.currentTarget.style.display = ""; }}
                />
                <input
                  ref={heroRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  data-testid="branding-hero-input"
                  className="hidden"
                  onChange={(e) => pickFile(e.target.files?.[0], setHeroFile, setHeroPreview, heroRef)}
                />
                <Button
                  type="button"
                  variant="outline"
                  data-testid="branding-hero-choose"
                  onClick={() => heroRef.current?.click()}
                  className="border-white/15 bg-white/5 text-white hover:bg-white/10"
                >
                  <Upload className="mr-1.5 h-4 w-4" /> Choose photo
                </Button>
                {heroPreview && (
                  <>
                    <img src={heroPreview} alt="New sign-in preview" className="h-20 w-32 rounded-md border border-white/15 object-cover" data-testid="branding-hero-new-preview" />
                    <Button
                      type="button"
                      data-testid="branding-hero-upload"
                      disabled={uploadingHero}
                      onClick={uploadHero}
                      className="bg-[#1a3fd1] text-white hover:bg-[#1a3fd1]/85"
                    >
                      {uploadingHero ? "Uploading…" : "Upload & apply"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- hero videos ---- */}
      <div data-testid="branding-hero-videos">
        <p className="mb-1 text-xs font-bold uppercase tracking-wider text-white/50">Hero videos (AI estimating)</p>
        <p className="mb-3 text-[11px] leading-relaxed text-white/40">
          These play at the top of the Upload Plans dialog and the Analyzing / Estimating overlays in the AI estimate tab.
          MP4, WebM or MOV, max 100 MB each. Any slot without a custom video uses the bundled default. Shared by all brands.
        </p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <VideoSlotUploader slot="upload" label="Upload dialog" url={branding?.video_upload_url || ""} busyPct={videoBusy.upload} onUpload={(f) => uploadVideo("upload", f)} onReset={() => resetVideo("upload")} />
          <VideoSlotUploader slot="analyze" label="Analyze overlay" url={branding?.video_analyze_url || ""} busyPct={videoBusy.analyze} onUpload={(f) => uploadVideo("analyze", f)} onReset={() => resetVideo("analyze")} />
          <VideoSlotUploader slot="estimate" label="Estimate overlay" url={branding?.video_estimate_url || ""} busyPct={videoBusy.estimate} onUpload={(f) => uploadVideo("estimate", f)} onReset={() => resetVideo("estimate")} />
        </div>
      </div>

      <div className="flex items-start gap-2 text-[11px] leading-relaxed text-white/40">
        <MonitorSmartphone className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          Everything on this page applies instantly — including on a self-hosted server, no republish needed.
          Phones/computers that already installed the app keep the old name and icon until the app is re-added to the home screen.
        </p>
      </div>
    </div>
  );
}
